import { useEffect, useRef, useState } from "react";

interface LazyMapProps {
  /** Map query, e.g. "Melbourne Victoria Australia" or "Mornington VIC". */
  query?: string;
  /** Google Maps embed zoom level. */
  zoom?: number;
  title: string;
  /** CSS height for the map area. */
  height?: string | number;
  className?: string;
}

/**
 * Lazily-mounted Google Maps embed.
 *
 * A map iframe is one of the heaviest things on a page (hundreds of KB of
 * scripts + tiles, extra connections). We never render it on first paint —
 * instead we show a lightweight placeholder and only swap in the real iframe
 * once it scrolls near the viewport (IntersectionObserver, 300px rootMargin).
 * That keeps the initial load fast while the map still appears "ready" by the
 * time the user reaches it. Falls back to mounting immediately if
 * IntersectionObserver is unavailable.
 */
export function LazyMap({
  query = "Melbourne Victoria Australia",
  zoom = 8,
  title,
  height = 380,
  className = "",
}: LazyMapProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (visible) return;
    const node = ref.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "300px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [visible]);

  const src = `https://www.google.com/maps?q=${encodeURIComponent(query)}&z=${zoom}&output=embed`;
  const h = typeof height === "number" ? `${height}px` : height;

  return (
    <div ref={ref} className={`lazymap ${className}`} style={{ minHeight: h }}>
      {visible ? (
        <iframe
          title={title}
          src={src}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          className="lazymap__iframe"
          style={{ height: h }}
        />
      ) : (
        <div className="lazymap__ph" style={{ height: h }} aria-hidden="true">
          <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
            <path d="M12 21s-7-5.6-7-11a7 7 0 0 1 14 0c0 5.4-7 11-7 11Z" />
            <circle cx="12" cy="10" r="2.5" />
          </svg>
          <span>Loading map…</span>
        </div>
      )}
      <style>{`
        .lazymap { position: relative; width: 100%; background: var(--band); }
        .lazymap__iframe { width: 100%; border: 0; display: block; }
        .lazymap__ph {
          display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px;
          color: var(--muted-text); font-family: var(--font-mono); font-size: 12px;
          text-transform: uppercase; letter-spacing: 0.05em;
          background: repeating-linear-gradient(-45deg, var(--band), var(--band) 14px, var(--border-strong) 14px, var(--border-strong) 28px);
        }
      `}</style>
    </div>
  );
}
